import { Head, useForm } from '@inertiajs/react'
import AdminLayout from '../../components/layout/AdminLayout'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import Toggle from '../../components/ui/Toggle'
import MultiSelect from '../../components/ui/MultiSelect'

const PROPERTY_TYPES = [
  { value: 'appartement', label: 'Appartement' },
  { value: 'maison', label: 'Maison' },
  { value: 'terrain', label: 'Terrain' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'bureaux', label: 'Bureaux' }
]

export default function CollaborationSettings({ agency, stats, partnerships }) {
  const { data, setData, patch, processing, errors } = useForm({
    collaboration_enabled: agency.collaboration_enabled || false,
    collaboration_radius_km: agency.collaboration_radius_km || 10,
    internal_priority_hours: agency.internal_priority_hours || 48,
    share_sales: agency.collaboration_rules?.share_sales ?? true,
    share_rentals: agency.collaboration_rules?.share_rentals ?? true,
    property_types: agency.collaboration_rules?.property_types || [],
    min_price: agency.collaboration_rules?.min_price || '',
    max_price: agency.collaboration_rules?.max_price || '',
    commission_split_source: agency.collaboration_rules?.commission_split_default?.source || 60,
    commission_split_target: agency.collaboration_rules?.commission_split_default?.target || 40,
    whitelist_only: agency.collaboration_rules?.whitelist_only || false
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    patch('/collaboration/settings')
  }

  return (
    <AdminLayout>
      <Head title="Paramètres de collaboration" />

      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Paramètres de collaboration</h1>
          <p className="text-gray-600 mt-1">
            Configurez les règles de partage de leads et propriétés avec d'autres agences
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Activation globale */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Activation</h2>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">Activer la collaboration</h3>
                <p className="text-sm text-gray-600">
                  Permettre le partage de propriétés avec d'autres agences du réseau
                </p>
              </div>
              <Toggle
                label="Activer la collaboration"
                checked={data.collaboration_enabled}
                onChange={(checked) => setData('collaboration_enabled', checked)}
              />
            </div>
          </div>

          {/* Paramètres géographiques */}
          {data.collaboration_enabled && (
            <>
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Paramètres géographiques</h2>

                <div className="space-y-4">
                  <Input
                    label="Rayon de collaboration (km)"
                    type="number"
                    min="0"
                    max="500"
                    value={data.collaboration_radius_km}
                    onChange={(e) => setData('collaboration_radius_km', parseInt(e.target.value))}
                    error={errors.collaboration_radius_km}
                    help="Distance maximale pour partager avec d'autres agences"
                  />

                  <Input
                    label="Fenêtre de priorité interne (heures)"
                    type="number"
                    min="0"
                    max="168"
                    value={data.internal_priority_hours}
                    onChange={(e) => setData('internal_priority_hours', parseInt(e.target.value))}
                    error={errors.internal_priority_hours}
                    help="Durée pendant laquelle les leads restent exclusifs à votre agence"
                  />
                </div>
              </div>

              {/* Types de transactions */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Types de transactions</h2>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">Partager les ventes</h3>
                      <p className="text-sm text-gray-600">Permettre les matches pour ventes</p>
                    </div>
                    <Toggle
                      label="Partager les ventes"
                      checked={data.share_sales}
                      onChange={(checked) => setData('share_sales', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">Partager les locations</h3>
                      <p className="text-sm text-gray-600">Permettre les matches pour locations</p>
                    </div>
                    <Toggle
                      label="Partager les locations"
                      checked={data.share_rentals}
                      onChange={(checked) => setData('share_rentals', checked)}
                    />
                  </div>
                </div>
              </div>

              {/* Types de propriétés */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Types de biens</h2>

                <MultiSelect
                  label="Types de propriétés à partager"
                  options={PROPERTY_TYPES}
                  value={data.property_types}
                  onChange={(values) => setData('property_types', values)}
                  error={errors.property_types}
                  help="Sélectionnez les types de biens que vous acceptez de partager"
                />
              </div>

              {/* Fourchette de prix */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Fourchette de prix</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Prix minimum (€)"
                    type="number"
                    min="0"
                    value={data.min_price}
                    onChange={(e) => setData('min_price', e.target.value)}
                    error={errors.min_price}
                    placeholder="Ex: 100000"
                  />

                  <Input
                    label="Prix maximum (€)"
                    type="number"
                    min="0"
                    value={data.max_price}
                    onChange={(e) => setData('max_price', e.target.value)}
                    error={errors.max_price}
                    placeholder="Ex: 1000000"
                  />
                </div>
              </div>

              {/* Commission split */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Répartition de commission par défaut
                </h2>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Votre part (%)"
                      type="number"
                      min="0"
                      max="100"
                      value={data.commission_split_source}
                      onChange={(e) => {
                        const source = parseInt(e.target.value)
                        setData({
                          ...data,
                          commission_split_source: source,
                          commission_split_target: 100 - source
                        })
                      }}
                      error={errors.commission_split_source}
                    />

                    <Input
                      label="Part agence cible (%)"
                      type="number"
                      value={data.commission_split_target}
                      disabled
                      help="Calculé automatiquement"
                    />
                  </div>

                  <p className="text-sm text-gray-600">
                    Cette répartition s'applique par défaut. Vous pouvez la personnaliser pour chaque partenaire.
                  </p>
                </div>
              </div>

              {/* Mode whitelist */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Mode de collaboration</h2>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">Partenaires approuvés uniquement</h3>
                    <p className="text-sm text-gray-600">
                      Collaborer uniquement avec les agences que vous avez approuvées
                    </p>
                  </div>
                  <Toggle
                    label="Partenaires approuvés uniquement"
                    checked={data.whitelist_only}
                    onChange={(checked) => setData('whitelist_only', checked)}
                  />
                </div>
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-4">
            <Button
              variant="outline"
              href="/collaboration/partners"
            >
              Gérer les partenaires
            </Button>

            <Button
              type="submit"
              disabled={processing}
              variant="primary"
            >
              {processing ? 'Enregistrement...' : 'Enregistrer les paramètres'}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  )
}
